import React, { useEffect, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import Link from 'next/link';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useLocaleLink } from '@/ui/hooks/useLocaleLink';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import { useTranslations } from 'next-intl';
import { CrystallizeShapeHelper, ShapeContent } from '@/utils/crystallize-shape-helper';
import Button from '@/ui/components/shared/Button';
import clsx from 'clsx';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { CollapsableAlert } from '@/ui/components/collapsable-alert/collapsable-alert';
import { RichTextRenderer } from '@/ui/components/tileRenderers/RichTextRenderer';
import { TileFragmentFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ContentTiles } from '@/ui/components/content-tiles/content-tiles';
import {
  extractContentTileItems,
  fetchContentTiles,
} from '@/ui/components/content-tiles/content-tiles-utils';
import { getResultStepCrystallizeInfo } from '../server-utils';
import {
  Compatibility,
  CylinderOverlapAnswer,
  EmergencyFeatureAnswer,
  ICFormAnswers,
  RealEstateUsageAnswer,
} from '../types';
import {
  fetchProductRecommendation,
  getEuResultStep,
  getResultScreenCrystallizePath,
  getUsResultStep,
} from '../utils';
import { MAX_INNER_CYLINDER_LENGTH, MAX_OUTER_CYLINDER_LENGTH } from '../constants';
import { SlotItem, SlotTextSwitcher } from '../../../shared/SlotTexSwitcher/SlotTextSwitcher';
import RecommendedProduct, {
  RecommendedProductFeature,
  RecommendedProductProps,
} from '../ICRecommendedProduct/RecommendedProduct';
import BubbleQuestionMarkSvg from '../../../../../../public/images/bubble-question-mark.svg';
import PromotionSlot from '../promotion-slot/promotion-slot';
import { extractPromotionItems } from '../promotion-slot/promotion-slot-utils';
import { PromotionItem } from '../promotion-slot/types';

const NOTE_ICONS: Record<string, React.ReactNode> = {
  warning: <ExclamationTriangleIcon className="h-6 w-6 text-primary-100" />,
  'bubble-question-mark': (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={BubbleQuestionMarkSvg.src} alt="" width={24} height={24} />
  ),
};

export interface Option {
  text: string;
  value: string;
}

export interface CompatibilityScreenProps {
  isUS?: boolean;
  installationCheckAnswers: ICFormAnswers;
  mainText: string;
  subtext1?: string;
  subtext2?: React.ReactNode;
  button1?: Option;
  button2?: Option;
  noteTitle?: string;
  learnMore?: string;
  learnMoreLink?: string;
  image?: string;
  onExtendFeatures?: (isTopChoice: boolean) => void;
  onExtendReasons?: (isTopChoice: boolean) => void;
  onSelect: (params: {
    value?: any;
    product?: { path: string; name: string; isTopChoice?: boolean };
  }) => void;
  onPromoDoubleOptInSubmitted?: () => void;
  onPromoVoucherCopy?: () => void;
  onPromoLinkClick?: () => void;
  compatibility?: string;
  b2b: boolean;
  limitations?: string[];
  language: string;
  title?: React.ReactNode;
}

const getVerticalRotationElements = (flattenedCryData: any): SlotItem[] =>
  flattenedCryData
    .map((element: any) => {
      const iconUrl = element?.image?.images?.[0]?.url!;
      const text = element?.text;

      if (!iconUrl || !text) {
        return;
      }

      return {
        iconUrl,
        text,
      };
    })
    .filter(Boolean);

const extractFeatures = (featuresChunk: any): RecommendedProductFeature[] => {
  if (!featuresChunk) {
    return [];
  }

  const features = featuresChunk?.map((feature: any) => {
    const imageUrl = feature?.components?.find((f: any) => f.id === 'icon')?.content?.images[0]
      ?.url;
    const text = feature?.components?.find((f: any) => f.id === 'text')?.content?.plainText[0];

    return {
      imageUrl,
      text,
    };
  });

  return features || [];
};

/**
 * @param document any Crystallize type that has hreflangs support (document or product)
 */
const getPathForLang = (document: any, language: string, fallbackPath: string) => {
  const productPath =
    document?.paths?.hreflangs?.find((hreflang: any) => hreflang.language === language)?.path ||
    fallbackPath;
  return productPath;
};

export function CompatibilityScreen({
  isUS = false,
  installationCheckAnswers,
  button1,
  button2,
  subtext1,
  subtext2,
  noteTitle,
  learnMore,
  learnMoreLink,
  compatibility,
  image,
  onExtendFeatures,
  onExtendReasons,
  onSelect,
  onPromoDoubleOptInSubmitted,
  onPromoVoucherCopy,
  onPromoLinkClick,
  limitations,
  language,
  title,
}: Readonly<CompatibilityScreenProps>) {
  const localization = useLocalization();
  const { localizedLink } = useLocaleLink();
  const [allRecommendedItems, setRecommendedItems] = useState<RecommendedProductProps[]>([]);
  const [topChoicePath, setTopChoicePath] = useState<string | undefined>(undefined);
  const [cryResultsFlattened, setCryResultsFlattened] = useState<any>(undefined);
  const [isLoading, setIsLoading] = useState(compatibility !== Compatibility.BAD);
  const [slotItems, setSlotItems] = useState<SlotItem[]>([]);
  const [promotions, setPromotions] = useState<Record<string, PromotionItem[]>>({});
  const [contentTiles, setContentTiles] = useState<Record<string, TileFragmentFragment[]>>({});
  const t = useTranslations('IC_Survey');

  const cleanPath = (path: string) => path.split('?')[0];

  const topChoices = allRecommendedItems.filter((ri) => cleanPath(ri.path) === topChoicePath);
  const nonTopChoices = allRecommendedItems.filter((ri) => cleanPath(ri.path) !== topChoicePath);
  const nonTopChoiceSectionTitle = cryResultsFlattened?.['recommended-products-section-title'];
  const hasEmergencyFeature =
    installationCheckAnswers?.icSurvey_emergencyFeature === EmergencyFeatureAnswer.YES;
  const overlapUpTo3mm =
    installationCheckAnswers?.icSurvey_cylinderOverlap === CylinderOverlapAnswer.UP_TO_3;

  // EU flow only: Check if cylinder extension is needed (measurements above EU thresholds)
  const cylinderExtensionNeeded =
    !isUS &&
    (Number(installationCheckAnswers?.icSurvey_insideCylinderLength) >= MAX_INNER_CYLINDER_LENGTH ||
      Number(installationCheckAnswers?.icSurvey_outsideCylinderLength) >=
        MAX_OUTER_CYLINDER_LENGTH);

  useEffect(() => {
    if (compatibility === Compatibility.BAD) {
      return;
    }

    setIsLoading(true);

    const fetchRecommendationItems = async () => {
      try {
        const productPaths: string[] = [];
        let allProductRecommendations: Partial<RecommendedProductProps>[] = [];

        // Determine the result step for fetching the right Crystallize document
        const resultStepName = isUS
          ? getUsResultStep(installationCheckAnswers)
          : getEuResultStep(installationCheckAnswers);

        const resultStepCrystallizeDocumentPath = getResultScreenCrystallizePath(resultStepName);

        // Unfortunately due to Crystallize limitations we need to fetch 2 times the data, unless
        // the language is 'en'. First time we fetch for 'en' as this is default language.
        // Second time for the language we need the data for.
        let resultsCryData = await getResultStepCrystallizeInfo(
          resultStepCrystallizeDocumentPath,
          'en',
        );

        // if 'en', recheck is not needed
        if (language !== 'en') {
          resultsCryData = await getResultStepCrystallizeInfo(
            getPathForLang(resultsCryData.catalogue, language, resultStepCrystallizeDocumentPath),
            language,
          );
        }

        // flatten Crystallize data for easier handling
        const crystallizeFlattener = new CrystallizeShapeHelper(
          resultsCryData.catalogue as ShapeContent,
        );
        const crystallizeDataFlattened = crystallizeFlattener.getFlattened();
        setCryResultsFlattened(crystallizeDataFlattened);

        const components = resultsCryData.catalogue?.components ?? undefined;
        setPromotions({
          topOfPage: extractPromotionItems(components, 'promotion-top-of-page'),
          belowHeader: extractPromotionItems(components, 'promotion-below-header'),
          belowTopChoice: extractPromotionItems(components, 'promotion-below-top-choice'),
          endOfPage: extractPromotionItems(components, 'promotion-end-of-page'),
        });

        // Content-field tiles are item relations; each linked tile is fetched with the full tile
        // fragment so they render like on a regular content page.
        const contentTileTopPaths = extractContentTileItems(components, 'content-tiles-top')
          .map((ref) => getPathForLang(ref, language, ref.path ?? ''))
          .filter((path): path is string => Boolean(path));

        const contentTileBottomPaths = extractContentTileItems(components, 'content-tiles-bottom')
          .map((ref) => getPathForLang(ref, language, ref.path ?? ''))
          .filter((path): path is string => Boolean(path));

        const [contentTilesTop, contentTilesBottom] = await Promise.all([
          fetchContentTiles(language, localization.areaVariables, contentTileTopPaths),
          fetchContentTiles(language, localization.areaVariables, contentTileBottomPaths),
        ]);
        setContentTiles({ top: contentTilesTop, bottom: contentTilesBottom });

        const topChoiceProduct =
          crystallizeDataFlattened?.['top-choice']?.[0]?.['top-choice-product']?.[0];

        const nonTopChoiceRecommendedProducts = crystallizeDataFlattened?.[
          'recommended-products'
        ]?.flatMap((p: { product: any }) => p.product || []);

        // top choice must be first
        if (topChoiceProduct?.path) {
          const topChoicePathForLang = getPathForLang(
            topChoiceProduct,
            language,
            topChoiceProduct.path,
          );
          productPaths.push(topChoicePathForLang);
          setTopChoicePath(topChoicePathForLang);
        }

        // add non top choice elements
        if (nonTopChoiceRecommendedProducts?.length > 0) {
          nonTopChoiceRecommendedProducts.forEach((rp: any) => {
            const pathForLang = getPathForLang(rp, language, rp.path);
            productPaths.push(pathForLang);
          });
        }

        // includes top choice as well as other products
        allProductRecommendations = await fetchProductRecommendation(
          language,
          localization.areaVariables,
          productPaths,
          installationCheckAnswers?.icSurvey_cylinderType,
        );

        // handle rotating elements
        const rotatingElements = getVerticalRotationElements(
          crystallizeDataFlattened?.['vertical-rotating-element'] || [],
        );
        setSlotItems(rotatingElements);

        setRecommendedItems(allProductRecommendations as RecommendedProductProps[]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendationItems();
  }, []);

  if (isLoading) {
    return (
      <div className="flex w-full flex-grow flex-col items-center justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex w-full flex-grow flex-col items-center gap-4 text-center">
      <ContentTiles
        tiles={contentTiles.top ?? []}
        className="-mx-4 self-stretch text-left lg:-mx-8"
      />

      <PromotionSlot
        items={promotions.topOfPage ?? []}
        onPromoDoubleOptInSubmitted={onPromoDoubleOptInSubmitted}
        onPromoVoucherCopy={onPromoVoucherCopy}
        onPromoLinkClick={onPromoLinkClick}
      />

      {title && (
        <h1 className="mx-auto mb-2 w-full max-w-lg text-center text-2xl font-semibold lg:text-3xl xl:max-w-xl xl:text-4xl">
          {title}
        </h1>
      )}

      {image && <img src={image} alt="Compatibility Result" className="w-full max-w-lg" />}

      {compatibility !== Compatibility.BAD && slotItems.length > 0 && (
        <div className="mb-2">
          <SlotTextSwitcher items={slotItems} />
        </div>
      )}

      {subtext1 && (
        <p className="mt-4">
          {subtext1}
          {learnMore && learnMoreLink && (
            <span>
              {' '}
              <Link href={learnMoreLink} className="text-start underline">
                {learnMore}
              </Link>
            </span>
          )}
        </p>
      )}

      {subtext2 && <p className="mt-4 max-w-3xl">{subtext2}</p>}

      <PromotionSlot
        items={promotions.belowHeader ?? []}
        onPromoDoubleOptInSubmitted={onPromoDoubleOptInSubmitted}
        onPromoVoucherCopy={onPromoVoucherCopy}
        onPromoLinkClick={onPromoLinkClick}
      />

      <div className="mt-4 flex w-full flex-col flex-wrap items-center gap-4">
        {/* top choice */}
        {topChoices.length > 0 && (
          <div className="w-full max-w-lg">
            {topChoices.map((item, i) => (
              <RecommendedProduct
                topChoice={t('Top Choice')}
                key={i}
                {...item}
                name={cryResultsFlattened?.['top-choice']?.[0]?.['product-title'] || item.name}
                reasons={cryResultsFlattened?.['top-choice']?.[0]?.['top-choice-reasons'].json.map(
                  (r: any) => r.textContent,
                )}
                productFeatures={extractFeatures(
                  cryResultsFlattened?.['top-choice']?.[0]?.features,
                )}
                onExtendFeatures={(isTopChoice) => {
                  if (onExtendFeatures) {
                    onExtendFeatures(isTopChoice);
                  }
                }}
                onExtendReasons={(isTopChoice) => {
                  if (onExtendReasons) {
                    onExtendReasons(isTopChoice);
                  }
                }}
                onSelect={(path, name, isTopChoice) =>
                  onSelect({ product: { path, name, isTopChoice } })
                }
              />
            ))}
          </div>
        )}
        <div className="-mt-4 w-full max-w-lg">
          {cryResultsFlattened?.notes?.map((note: any, i: number) => {
            if (!note?.['show-note']) {
              return null;
            }

            const noteTypes: string[] = note?.type || [];
            let showNote = false;

            // No type selected = always show
            if (noteTypes.length === 0) {
              showNote = true;
            }

            // Smart hosting: only for rental users
            if (noteTypes.includes('smart-hosting')) {
              if (
                installationCheckAnswers?.icSurvey_realEstateUsage !== RealEstateUsageAnswer.PRIVATE
              ) {
                showNote = true;
              }
            }

            // Restriction: no emergency function
            if (!hasEmergencyFeature && noteTypes.includes('restriction-no-emergency-function')) {
              showNote = true;
            }

            // Restriction: cylinder overlap under 3mm
            if (overlapUpTo3mm && noteTypes.includes('restriction-overlap-limit-under-3mm')) {
              showNote = true;
            }

            // Restriction: cylinder extension needed (US measurements in extension range)
            if (
              cylinderExtensionNeeded &&
              noteTypes.includes('restriction-cylinder-extension-needed')
            ) {
              showNote = true;
            }

            if (!showNote) {
              return null;
            }

            const iconKey = note?.icon?.[0] || 'warning';
            const internalLinkItem = note?.['cta-internal-link']?.[0];
            const internalPath = internalLinkItem
              ? getPathForLang(internalLinkItem, language, internalLinkItem.path)
              : undefined;
            const ctaUrl = internalPath ? localizedLink(internalPath) : note?.['cta-external-link'];

            return (
              <CollapsableAlert
                key={`note-${i}`}
                title={note?.heading}
                description={<RichTextRenderer blocks={note?.text} />}
                icon={NOTE_ICONS[iconKey]}
                ctaText={note?.['cta-text']}
                ctaUrl={ctaUrl}
              />
            );
          })}
        </div>

        <PromotionSlot
          items={promotions.belowTopChoice ?? []}
          onPromoDoubleOptInSubmitted={onPromoDoubleOptInSubmitted}
          onPromoVoucherCopy={onPromoVoucherCopy}
          onPromoLinkClick={onPromoLinkClick}
        />

        {/* non top choices */}
        {nonTopChoices.length > 0 && (
          <div className="w-full max-w-lg">
            <div
              className={clsx('-mx-6 flex flex-col gap-4 p-6', {
                'bg-neutral-5': topChoices && topChoices.length > 0,
              })}
            >
              {nonTopChoiceSectionTitle && (
                <h2 className="mb-7 text-lg font-semibold md:text-2xl">
                  {nonTopChoiceSectionTitle}
                </h2>
              )}
              {nonTopChoices.map((item, i) => (
                <RecommendedProduct
                  key={i}
                  {...item}
                  name={
                    cryResultsFlattened?.['recommended-products']?.[i]?.['product-title'] ||
                    item.name
                  }
                  productFeatures={extractFeatures(
                    cryResultsFlattened?.['recommended-products']?.[i]?.features,
                  )}
                  onExtendFeatures={(isTopChoice) => {
                    if (onExtendFeatures) {
                      onExtendFeatures(isTopChoice);
                    }
                  }}
                  onSelect={(path, name, isTopChoice) =>
                    onSelect({ product: { path, name, isTopChoice } })
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <PromotionSlot
        items={promotions.endOfPage ?? []}
        onPromoDoubleOptInSubmitted={onPromoDoubleOptInSubmitted}
        onPromoVoucherCopy={onPromoVoucherCopy}
        onPromoLinkClick={onPromoLinkClick}
      />

      <ContentTiles
        tiles={contentTiles.bottom ?? []}
        className="-mx-4 self-stretch text-left lg:-mx-8"
      />

      {noteTitle && limitations && limitations.length > 0 && (
        <div className="mt-4 rounded-md bg-primary-30 p-2">
          <div className="flex items-center gap-3 text-start">
            <ExclamationTriangleIcon className="h-8 w-8 text-primary-100" />
            <p className="font-bold">{noteTitle}</p>
          </div>
          <ul className="-mt-1 list-outside list-disc pb-2 pl-15 text-left">
            {limitations.map((limitation, index) => {
              if (limitation) {
                return <li key={index}>{limitation}</li>;
              }
              return null;
            })}
          </ul>
        </div>
      )}

      {(button1 || button2) && (
        <div className="sticky bottom-0 z-10 -mx-10 mt-auto flex w-full flex-col space-y-4 bg-white px-4 py-4 lg:static lg:mx-0 lg:w-auto lg:px-0 ">
          {button1 &&
            (compatibility === Compatibility.BAD ? (
              <Button
                key={button1.value}
                variant="whiteBlackBorder"
                onClick={() => onSelect({ value: button1.value })}
              >
                <ArrowPathIcon className="mr-2 h-4 w-4" />
                {button1.text}
              </Button>
            ) : (
              <Button
                type="button"
                variant="quinary"
                key={button1.value}
                className="px-6 py-3"
                onClick={() => onSelect({ value: button1.value })}
              >
                {button1.text}
              </Button>
            ))}
          {button2 && (
            <Button
              type="button"
              key={button2.value}
              variant="yellowBlackBorder"
              className="px-6 py-3"
              onClick={() => onSelect({ value: button2.value })}
            >
              {button2.text}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
